import { useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { legacyOffersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  Save
} from "lucide-react";

export default function LegacyOfferImportPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [step, setStep] = useState("upload"); // upload, review, saving
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  
  // Parsed data from import
  const [importedOffer, setImportedOffer] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  
  // Editable form data
  const [formData, setFormData] = useState({
    title: "",
    document_type: "angebot",
    offer_reference: "",
    project_number: "",
    document_date: "",
    net_amount: 0,
    vat_rate: 19,
    vat_amount: 0,
    gross_amount: 0,
    free_text_header: "",
    free_text_body: "",
    free_text_footer: "",
    notes_internal: ""
  });

  const handleFileDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer?.files || e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Nur PDF-Dateien werden unterstützt");
      return;
    }
    
    await uploadFile(file);
  }, [customerId]);

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("customer_id", customerId);
      
      const response = await legacyOffersAPI.import(formDataUpload);
      const offer = response.data;
      
      setImportedOffer(offer);
      setPdfPreviewUrl(offer.pdf_preview_url);
      
      // Populate form with parsed data
      setFormData({
        title: offer.title || file.name.replace(".pdf", ""),
        document_type: offer.document_type || "angebot",
        offer_reference: offer.offer_reference || "",
        project_number: offer.project_number || "",
        document_date: offer.document_date?.split("T")[0] || "",
        net_amount: offer.net_amount || 0,
        vat_rate: offer.vat_rate ?? 19,
        vat_amount: offer.vat_amount || 0,
        gross_amount: offer.gross_amount || 0,
        discount_type: offer.discount_type || "keine",
        discount_value: offer.discount_value || 0,
        free_text_header: offer.free_text_header || "",
        free_text_body: offer.free_text_body || "",
        free_text_footer: offer.free_text_footer || "",
        notes_internal: ""
      });
      
      setStep("review");
      
      // Show parsing method info
      if (offer.import_parse_status === "ai") {
        toast.success("PDF mit KI analysiert", {
          description: "Die Daten wurden mit KI-Unterstützung extrahiert. Bitte überprüfen Sie die Werte."
        });
      } else if (offer.import_parse_status === "text") {
        toast.success("PDF analysiert", {
          description: "Text wurde aus dem PDF extrahiert. Bitte überprüfen Sie die Werte."
        });
      } else {
        toast.info("PDF hochgeladen", {
          description: "Bitte geben Sie die Daten manuell ein."
        });
      }
      
    } catch (error) {
      console.error("Failed to import PDF:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Import");
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate VAT and gross when net changes
      if (field === "net_amount" || field === "vat_rate") {
        const net = field === "net_amount" ? value : prev.net_amount;
        const rate = field === "vat_rate" ? value : prev.vat_rate;
        updated.vat_amount = Math.round(net * (rate / 100) * 100) / 100;
        updated.gross_amount = Math.round((net + updated.vat_amount) * 100) / 100;
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    if (!importedOffer) return;
    
    try {
      setSaving(true);
      
      await legacyOffersAPI.update(importedOffer.id, formData);
      toast.success("Legacy-Angebot gespeichert");
      navigate(`/legacy-angebote/${importedOffer.id}`);
      
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const ParseMethodBadge = () => {
    if (!importedOffer) return null;
    
    const status = importedOffer.import_parse_status;
    const confidence = importedOffer.import_parse_confidence || 0;
    
    if (status === "ai") {
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <Sparkles className="h-3 w-3 mr-1" />
          KI-Analyse ({Math.round(confidence * 100)}% Konfidenz)
        </Badge>
      );
    }
    if (status === "text") {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <FileText className="h-3 w-3 mr-1" />
          Text-Extraktion ({Math.round(confidence * 100)}% Konfidenz)
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Manuelle Eingabe erforderlich
      </Badge>
    );
  };

  // Upload Step
  if (step === "upload") {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Legacy-Angebot importieren</h1>
            <p className="text-muted-foreground">PDF hochladen und Daten extrahieren</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>PDF-Dokument hochladen</CardTitle>
            <CardDescription>
              Laden Sie ein Angebot oder eine Auftragsbestätigung aus Ihrem alten System hoch.
              Das Dokument wird analysiert und die Daten automatisch extrahiert.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragOver ? "border-primary bg-primary/5" : "border-gray-300"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
            >
              {uploading ? (
                <div className="space-y-4">
                  <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" />
                  <p className="text-lg">PDF wird analysiert...</p>
                  <p className="text-sm text-muted-foreground">
                    Dies kann einen Moment dauern, wenn KI-Analyse erforderlich ist.
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg mb-2">PDF hierher ziehen</p>
                  <p className="text-muted-foreground mb-4">oder</p>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileDrop}
                    className="hidden"
                    id="pdf-upload"
                    data-testid="pdf-upload-input"
                  />
                  <Button asChild>
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      PDF auswählen
                    </label>
                  </Button>
                </>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">So funktioniert der Import:</h4>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>PDF wird hochgeladen und sicher gespeichert</li>
                <li>Text wird aus dem Dokument extrahiert</li>
                <li>Bei Bedarf wird KI zur Analyse eingesetzt</li>
                <li>Sie können alle erkannten Daten überprüfen und korrigieren</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Review Step - Split view with PDF and form
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setStep("upload")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">Import prüfen & korrigieren</h1>
              <ParseMethodBadge />
            </div>
            <p className="text-muted-foreground">
              Überprüfen Sie die extrahierten Daten und korrigieren Sie bei Bedarf
            </p>
          </div>
        </div>
        
        <Button onClick={handleSave} disabled={saving} size="lg" data-testid="btn-save-import">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Speichern & Fortfahren
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PDF Preview */}
        <Card className="h-fit lg:sticky lg:top-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Original-PDF
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pdfPreviewUrl ? (
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-[700px] border rounded-lg"
                title="PDF Vorschau"
              />
            ) : (
              <div className="h-[700px] flex items-center justify-center bg-muted rounded-lg">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Stammdaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titel</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    data-testid="input-import-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Dokumenttyp</Label>
                  <Select
                    value={formData.document_type}
                    onValueChange={(v) => handleInputChange("document_type", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="angebot">Angebot</SelectItem>
                      <SelectItem value="auftragsbestätigung">Auftragsbestätigung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Dokumentdatum</Label>
                  <Input
                    type="date"
                    value={formData.document_date}
                    onChange={(e) => handleInputChange("document_date", e.target.value)}
                    data-testid="input-import-date"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Angebotsnummer / Referenz</Label>
                  <Input
                    value={formData.offer_reference}
                    onChange={(e) => handleInputChange("offer_reference", e.target.value)}
                    placeholder="z.B. ANG-2024-001"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label>Projektnummer (extern)</Label>
                  <Input
                    value={formData.project_number}
                    onChange={(e) => handleInputChange("project_number", e.target.value)}
                    placeholder="z.B. P-2024-001"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Beträge</CardTitle>
              <CardDescription>
                Die MwSt. und der Bruttobetrag werden automatisch berechnet, wenn Sie den Nettobetrag ändern.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nettobetrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.net_amount}
                    onChange={(e) => handleInputChange("net_amount", parseFloat(e.target.value) || 0)}
                    data-testid="input-import-net"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>MwSt.-Satz (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.vat_rate}
                    onChange={(e) => handleInputChange("vat_rate", parseFloat(e.target.value) || 19)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>MwSt.-Betrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.vat_amount}
                    onChange={(e) => handleInputChange("vat_amount", parseFloat(e.target.value) || 0)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="font-semibold">Bruttobetrag (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.gross_amount}
                    onChange={(e) => handleInputChange("gross_amount", parseFloat(e.target.value) || 0)}
                    className="font-bold text-lg"
                    data-testid="input-import-gross"
                  />
                </div>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Berechneter Brutto:</span>
                  <span className="font-medium">
                    {formatCurrency(formData.net_amount + formData.vat_amount)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Leistungsbeschreibung</CardTitle>
              <CardDescription>
                Der extrahierte Text kann hier bearbeitet werden
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Kopftext</Label>
                <Textarea
                  value={formData.free_text_header}
                  onChange={(e) => handleInputChange("free_text_header", e.target.value)}
                  rows={2}
                  placeholder="Einleitungstext..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>Haupttext / Positionen</Label>
                <Textarea
                  value={formData.free_text_body}
                  onChange={(e) => handleInputChange("free_text_body", e.target.value)}
                  rows={8}
                  placeholder="Leistungsbeschreibung..."
                  data-testid="textarea-import-body"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fußtext</Label>
                <Textarea
                  value={formData.free_text_footer}
                  onChange={(e) => handleInputChange("free_text_footer", e.target.value)}
                  rows={2}
                  placeholder="Zahlungsbedingungen, Abschlusstext..."
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Interne Notizen</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.notes_internal}
                onChange={(e) => handleInputChange("notes_internal", e.target.value)}
                rows={3}
                placeholder="Interne Notizen zum Import (nicht auf Dokumenten sichtbar)..."
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pb-6">
            <Button variant="outline" onClick={() => setStep("upload")}>
              Zurück zum Upload
            </Button>
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Speichern & Fortfahren
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
